import React, { useRef, useState } from 'react';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AiImageReferenceInput } from '../types';

type WineSlotId = 'a' | 'b';

interface WineImageSlotsProps {
  imageA: AiImageReferenceInput | null;
  imageB: AiImageReferenceInput | null;
  onChange: (slot: WineSlotId, image: AiImageReferenceInput | null) => void;
  onNotify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

const SLOTS: Array<{ id: WineSlotId; title: 'Bild A' | 'Bild B'; description: string }> = [
  { id: 'a', title: 'Bild A', description: 'Flaska med blänk' },
  { id: 'b', title: 'Bild B', description: 'Etikett på flaska' },
];

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(file.name);
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Kunde inte läsa filen ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function fileToReference(file: File, title: 'Bild A' | 'Bild B'): Promise<AiImageReferenceInput> {
  return readFileAsDataUrl(file).then((dataUrl) => ({
    referenceId: `wine-${title === 'Bild A' ? 'a' : 'b'}-${Date.now()}`,
    type: 'upload',
    fileName: file.name,
    mimeType: file.type || 'image/jpeg',
    title,
    dataUrl,
  }));
}

const WineSlot: React.FC<{
  title: 'Bild A' | 'Bild B';
  description: string;
  image: AiImageReferenceInput | null;
  onChange: (image: AiImageReferenceInput | null) => void;
  onNotify: WineImageSlotsProps['onNotify'];
}> = ({ title, description, image, onChange, onNotify }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    inputRef.current?.click();
  };

  const acceptFile = async (file: File | undefined) => {
    if (!file) return;
    if (!isImageFile(file)) {
      onNotify('Släpp en bildfil i rutan.', 'warning');
      return;
    }
    try {
      onChange(await fileToReference(file, title));
    } catch (error) {
      onNotify(error instanceof Error ? error.message : 'Kunde inte läsa bilden.', 'error');
    }
  };

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        const file = Array.from(event.dataTransfer.files).find(isImageFile) || event.dataTransfer.files[0];
        void acceptFile(file);
      }}
      onClick={() => {
        if (!image) openPicker();
      }}
      sx={{
        minHeight: 210,
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: '1.5px dashed',
        borderColor: dragOver ? 'primary.main' : image ? 'success.main' : 'rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        cursor: image ? 'default' : 'pointer',
        outline: 'none',
        transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
        '&:focus-within': {
          borderStyle: 'solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 3px rgba(15,52,96,0.18)',
        },
        ...(dragOver ? { bgcolor: 'rgba(15,52,96,0.04)' } : {}),
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          void acceptFile(file);
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
        </Box>
        <Chip size="small" color={image ? 'success' : 'warning'} label={image ? 'Vald' : 'Saknas'} />
      </Box>
      {image?.dataUrl ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, flex: 1 }}>
          <Box
            component="img"
            src={image.dataUrl}
            alt={title}
            sx={{
              width: '100%',
              height: 140,
              objectFit: 'contain',
              borderRadius: 1.5,
              bgcolor: '#f3f4f6',
            }}
          />
          <Typography variant="caption" noWrap title={image.fileName || title}>
            {image.fileName || title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                openPicker();
              }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Byt bild
            </Button>
            <IconButton
              size="small"
              aria-label={`Ta bort ${title}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange(null);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            color: 'text.secondary',
            textAlign: 'center',
          }}
        >
          <CloudUploadIcon />
          <Typography variant="body2">Släpp en bild här</Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={(event) => {
              event.stopPropagation();
              openPicker();
            }}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Välj fil
          </Button>
        </Box>
      )}
    </Box>
  );
};

const WineImageSlots: React.FC<WineImageSlotsProps> = ({
  imageA,
  imageB,
  onChange,
  onNotify,
}) => {
  const images = { a: imageA, b: imageB };

  const complete = Boolean(imageA && imageB);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Chip
        size="small"
        color={complete ? 'success' : 'warning'}
        label={complete ? 'Båda bilderna är valda' : 'Ofullständig — båda bilderna behövs'}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
      {SLOTS.map((slot) => (
        <WineSlot
          key={slot.id}
          title={slot.title}
          description={slot.description}
          image={images[slot.id]}
          onChange={(image) => onChange(slot.id, image)}
          onNotify={onNotify}
        />
      ))}
      </Box>
    </Box>
  );
};

export default WineImageSlots;
